/**
 * Per-bridge connection manager.
 *
 * Adaptive `/health` polling — OPBridge is "up and down like a yoyo"
 * during active dev, so:
 *   - 5s steady-state when last poll succeeded
 *   - 2s recovery when the previous poll failed (fast detect of restart)
 *   - 10s backoff after `BACKOFF_THRESHOLD` consecutive failures (don't
 *     burn iPad battery on a long outage)
 *
 * State transitions log once (not per poll). That keeps the console clean
 * while OPBridge is bouncing every few seconds.
 *
 * One BridgeConnection instance per BridgeConfig. The manager (store.ts)
 * creates / destroys these as bridges are added / removed.
 */

import { createSignal, type Accessor } from 'solid-js';
import { OpbridgeHttpClient, OpbridgeError, type OpbridgeHealth } from './httpClient.ts';
import type { WtConnection } from './wtTransport.ts';

export type BridgeStatus =
  | 'unpaired'        // no bearer token configured yet
  | 'connecting'      // first /health request in flight
  | 'connected'       // last /health was ok
  | 'unauthorized'    // 401 — token rejected, needs re-pairing
  | 'unreachable';    // network/timeout error

// ONE MISS IS A BLIP, THREE IN A ROW IS AN OUTAGE (2026-09-30,
// next-update-plan 1.5). A single slow /health reply used to flip the
// bridge to unreachable at once, and every control on the rig disabled
// (FireButton `disabled={!live()}`) until the next good poll - while
// sends were working fine. Venue Wi-Fi produces exactly that one slow
// reply. A CONNECTED bridge now stays connected through isolated misses,
// so controls and sends stay live while degraded; the third miss in a row
// flips it, and everything that reads the status (controls, the BRIDGE
// LOST banner, the header badge, the syncs) flips together.
//
// Detection, worst case: bridge dies at T=0 right after a good tick →
// next tick at T=2s → 1.2s timeout → miss 1 at T=3.2s (still connected) →
// retry at 3.7s → miss 2 at 4.9s → retry at 5.4s → miss 3 at 6.6s →
// unreachable. Before this, the badge confirmed Offline at 5.9s (2 misses
// at a 1.5s retry); the faster 0.5s retry after a miss is what keeps the
// three-miss rule close to that. Typical case (bridge dies mid-cycle)
// averages well under that. Cost: more /health traffic only while misses
// are happening, and only until the backoff below.
const POLL_OK_MS       = 2_000;
const POLL_RECOVER_MS  = 500;
const POLL_BACKOFF_MS  = 10_000;
const BACKOFF_THRESHOLD = 6;     // ≈ 6 × (0.5s + up to 1.2s) ≈ 10s of failure before backoff
/** Misses in a row before a connected bridge counts as unreachable. */
const UNREACHABLE_AFTER_MISSES = 3;
// Kept comfortably shorter than POLL_RECOVER_MS so a slow probe never
// overlaps the next scheduled tick. LAN /health typically answers sub-
// 100ms; a 1200ms budget is generous while keeping the confirmed-offline
// path within the 5s alert budget.
const HEALTH_TIMEOUT_MS = 1_200;

export type HealthObserver = (h: OpbridgeHealth) => void;

export class BridgeConnection {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private consecutiveFailures = 0;
  private inFlight = false;

  private readonly _status = createSignal<BridgeStatus>('connecting');
  private readonly _health = createSignal<OpbridgeHealth | null>(null);
  private readonly _lastError = createSignal<string | null>(null);
  private readonly _lastCheckedMs = createSignal<number>(0);
  /** Consecutive-failure counter reflected as a signal so UI can bind
   *  "really offline" state to a threshold. Since 2026-09-30 a connected
   *  bridge's STATUS itself only flips after `UNREACHABLE_AFTER_MISSES`, so
   *  a threshold below that is never reached while it reads unreachable.
   *  Resets to 0 on any successful /health tick. */
  private readonly _consecutiveFailures = createSignal<number>(0);

  constructor(
    private readonly client: OpbridgeHttpClient,
    private readonly hasBearer: boolean,
    private readonly onHealth?: HealthObserver,
  ) {
    if (!hasBearer) {
      this._status[1]('unpaired');
    }
  }

  /** Start the polling loop. Idempotent. */
  start(): void {
    if (this.destroyed) return;
    if (this.timer !== null) return;
    void this.tick();
  }

  /** Stop polling and release the timer. Call when the bridge is removed
   *  from the user's list or when the manager is torn down. */
  destroy(): void {
    this.destroyed = true;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Live HTTP client. Exposed so panel actions (test MIDI, fixture CRUD)
   *  can issue requests without re-instantiating a client. */
  httpClient(): OpbridgeHttpClient { return this.client; }

  status(): BridgeStatus { return this._status[0](); }
  statusSignal(): Accessor<BridgeStatus> { return this._status[0]; }
  health(): OpbridgeHealth | null { return this._health[0](); }
  healthSignal(): Accessor<OpbridgeHealth | null> { return this._health[0]; }
  lastErrorSignal(): Accessor<string | null> { return this._lastError[0]; }
  lastCheckedMsSignal(): Accessor<number> { return this._lastCheckedMs[0]; }
  consecutiveFailuresSignal(): Accessor<number> { return this._consecutiveFailures[0]; }

  /** Fire a /health probe NOW. Resets the polling schedule from this point. */
  pokeNow(): void {
    if (this.destroyed) return;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    void this.tick();
  }

  private async tick(): Promise<void> {
    if (this.destroyed) return;
    if (this.inFlight) return; // belt-and-braces; shouldn't happen
    this.inFlight = true;
    try {
      // /health is unauthenticated. We don't need a bearer to ping liveness.
      const h = await this.client.health(HEALTH_TIMEOUT_MS);
      if (this.destroyed) return;
      this.consecutiveFailures = 0;
      this._consecutiveFailures[1](0);
      this._health[1](h);
      this._lastError[1](null);
      this._lastCheckedMs[1](Date.now());
      const next: BridgeStatus = this.hasBearer ? 'connected' : 'unpaired';
      this.transition(next);
      // Notify observer once per tick — store persists identity on first
      // successful poll; subsequent calls are idempotent.
      if (this.onHealth) {
        try { this.onHealth(h); } catch { /* observer must not break polling */ }
      }
      this.schedule(POLL_OK_MS);
    } catch (err) {
      if (this.destroyed) return;
      this.consecutiveFailures += 1;
      this._consecutiveFailures[1](this.consecutiveFailures);
      this._lastCheckedMs[1](Date.now());
      const kind = err instanceof OpbridgeError ? err.kind : 'network';
      const msg = err instanceof Error ? err.message : String(err);
      this._lastError[1](msg);
      // /health is unauthenticated so 'unauthorized' shouldn't fire here,
      // but if OPBridge tightens that later, surface it cleanly.
      // A connected bridge rides out isolated misses (see the constants
      // above); one that never connected has no "degraded" to protect.
      const degraded = kind !== 'unauthorized'
        && this._status[0]() === 'connected'
        && this.consecutiveFailures < UNREACHABLE_AFTER_MISSES;
      if (!degraded) this.transition(kind === 'unauthorized' ? 'unauthorized' : 'unreachable');
      const delay = this.consecutiveFailures >= BACKOFF_THRESHOLD
        ? POLL_BACKOFF_MS
        : POLL_RECOVER_MS;
      this.schedule(delay);
    } finally {
      this.inFlight = false;
    }
  }

  private schedule(delayMs: number): void {
    if (this.destroyed) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.tick();
    }, delayMs);
  }

  private transition(next: BridgeStatus): void {
    const prev = this._status[0]();
    if (prev === next) return;
    this._status[1](next);
    // Single line per state transition — won't spam the console while
    // OPBridge is being rebuilt mid-session.
    // eslint-disable-next-line no-console
    console.info(`[opbridge] ${prev} → ${next}`);
  }
}

export function makeConnection(
  baseUrl: string,
  bearer: string | null,
  onHealth?: HealthObserver,
  /** The bridge's WebTransport session, when it has a known-good offline
   *  path (see `store.ts::wtFor`). Passing it here means the health tick
   *  itself rides the offline transport - which is the point: a path
   *  exercised every 2 seconds at the desk cannot quietly rot into the
   *  night it is actually needed. `httpClient` falls back to `fetch` on a
   *  dial failure, so a dead WebTransport never shows up as a dead bridge. */
  wt: WtConnection | null = null,
): BridgeConnection {
  // No third arg here (2026-08-23 latency pass) - this client is SHARED for
  // every request issued through this connection (`conn.httpClient()`), not
  // just the health tick below. Passing `HEALTH_TIMEOUT_MS` here used to
  // make /health's tight 1200ms probe budget (sized for a sub-100ms LAN
  // liveness check under a 5s bridge-loss alert deadline) the DEFAULT
  // timeout for every MIDI send, engine push, doc sync, mixer/toggle/
  // timeline call, etc. issued through this same client - none of those
  // have anything to do with the health-alert budget. `client.health()`
  // now takes its own explicit timeout (see `tick()` below); everything
  // else falls through to `OpbridgeHttpClient`'s own considered general
  // default (3000ms).
  const client = new OpbridgeHttpClient(baseUrl, bearer, undefined, wt);
  return new BridgeConnection(client, bearer !== null, onHealth);
}
